import React from 'react';
import cn from 'classnames';
import { PointType, ValueType } from './types';
import styles from './track.module.scss';

interface IProps {
  children: React.ReactNode,
  disabled?: boolean,
  draggableTrack?: boolean,
  onTrackDrag?: (event: React.SyntheticEvent, prevEvent: React.SyntheticEvent) => void,
  onTrackMouseDown: (event: React.SyntheticEvent, position: PointType) => void,
  percentages: ValueType,
}

class Track extends React.Component<IProps> {
  // eslint-disable-next-line react/sort-comp
  private node: HTMLDivElement | null;

  private trackDragEvent: React.SyntheticEvent | null;

  constructor(props: IProps) {
    super(props);

    this.node = null;
    this.trackDragEvent = null;

    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
  }

  getClientRect(): DOMRect {
    return this.node.getBoundingClientRect();
  }

  getActiveTrackStyle(): { left: string, width: string } {
    const width = `${ (this.props.percentages.max - this.props.percentages.min) * 100 }%`;
    const left = `${ this.props.percentages.min * 100 }%`;

    return { left, width };
  }

  /**
   * Listen to mousemove event
   */
  addDocumentMouseMoveListener() {
    this.removeDocumentMouseMoveListener();
    // @ts-ignore
    this.node.ownerDocument.addEventListener('mousemove', this.handleMouseMove);
  }

  /**
   * Listen to mouseup event
   */
  addDocumentMouseUpListener() {
    this.removeDocumentMouseUpListener();
    this.node.ownerDocument.addEventListener('mouseup', this.handleMouseUp);
  }

  removeDocumentMouseMoveListener() {
    // @ts-ignore
    this.node.ownerDocument.removeEventListener('mousemove', this.handleMouseMove);
  }

  removeDocumentMouseUpListener() {
    this.node.ownerDocument.removeEventListener('mouseup', this.handleMouseUp);
  }

  handleMouseMove(event: React.SyntheticEvent) {
    if (!this.props.draggableTrack) {
      return;
    }

    if (this.trackDragEvent !== null) {
      this.props.onTrackDrag(event, this.trackDragEvent);
    }

    this.trackDragEvent = event;
  }

  handleMouseUp() {
    if (!this.props.draggableTrack) {
      return;
    }

    this.removeDocumentMouseMoveListener();
    this.removeDocumentMouseUpListener();
    this.trackDragEvent = null;
  }

  handleMouseDown(event: React.SyntheticEvent) {
    // @ts-ignore
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    const trackClientRect = this.getClientRect();
    const position = {
      x: clientX - trackClientRect.left,
      y: 0,
    };

    this.props.onTrackMouseDown(event, position);

    if (this.props.draggableTrack) {
      this.addDocumentMouseMoveListener();
      this.addDocumentMouseUpListener();
    }
  }

  handleTouchStart(event: React.SyntheticEvent) {
    event.preventDefault();

    this.handleMouseDown(event);
  }

  render() {
    const activeTrackStyle = this.getActiveTrackStyle();
    const trackCls = cn(styles.track, {
      [styles.isDisabled]: this.props.disabled,
      [styles.isActive]: !this.props.disabled,
    });
    return (
      <div
        className={ cn(styles.track, styles.trackBackground) }
        onMouseDown={ this.handleMouseDown }
        onTouchStart={ this.handleTouchStart }
        ref={ (node) => { this.node = node; } }
      >
        <div
          style={ activeTrackStyle }
          className={ trackCls }
        />
        { this.props.children }
      </div>
    );
  }
}

export default Track;
